const angular = require('angular')
const ng = angular.injector(['ng'])
const $q = ng.get('$q')
const $http = ng.get('$http')
const forEach = require('lodash/forEach')
const find = require('lodash/find')
const findIndex = require('lodash/findIndex')
const startsWith = require('lodash/startsWith')
const merge = require('lodash/merge')
const uniqBy = require('lodash/uniqBy')
const serverConfig = require('../infrastructure/serverConfig')

// 根据custom_domains解析当前域名对应的组织id
function parseDomainOrgId(domainBindings, hostname) {
  let matchOrgId = ''
  let matchLevelLength = 0
  /**
   * a.com => a
   * hk.a.com => hk
   * tw.a.com => tw
   */
  forEach(domainBindings, function (domainOrgId, domain) {
    const targetDomainLevels = domain.split('.')
    const currentDomainLevels = hostname.split('.')

    forEach(targetDomainLevels, function (level, index) {
      if (!currentDomainLevels[index] || level !== currentDomainLevels[index]) {
        /** 未匹配对应的域名层级则退出循环 */
        /** break loop when not matching corresponding level */
        return false
      }

      if (index === targetDomainLevels.length - 1 &&
        /** 所匹配的域名更精确 */
        /** matched domain has been more accurate */
        targetDomainLevels.length > matchLevelLength) {
        /** 匹配所有的域名层级则记录组织 id */
        /** mark organization id when match all levels */
        matchOrgId = domainOrgId
        matchLevelLength = targetDomainLevels.length
      }
    })
  })
  return matchOrgId
}

// 异步获取配置文件
function loadJSONSync(url) {
  const files = [].concat(url)

  const promises = []
  angular.forEach(files, path => {
    if (!/\.json$/gi.test(path)) return promises.push($q.resolve({}))

    promises.push($http({
      url: path + '?_t=' + new Date().valueOf(),
      method: 'GET'
    }).then(
      res => angular.isObject(res.data) ? res.data : {},
      err => {
        err.message = err.message || ''
        const msgTitle = ~err.message.indexOf('JSON at position') ? 'Failed to convert invalid json file' : 'Failed to load json file'
        const msgDetail = ~err.message.indexOf('JSON at position') ? err.message : err.status
        console.info(`${msgTitle}. ${msgDetail}. path: ${path}`)
        return {}
      })
    )
  })

  return promises.length > 1 ? $q.all(promises) : promises[0]
}

// 获取本地存储数据
function getLocalValue(type, name) {
  let localValue = null
  switch (type) {
    case 'cookie':
      const regExp = new RegExp('(^| )' + name + '=([^;]*)(;|$)')
      const arr = document.cookie.match(regExp)
      if (arr) localValue = decodeURIComponent(arr[2])
      break
    case 'localStorage':
      localValue = window.localStorage ? window.localStorage.getItem(name) : null
      break
    default:
      console.debug('getLocalValue type is error!')
  }
  return localValue
}

module.exports = {
  useCustomSettings: serverConfig.getItem('useCustomSettings'),
  matchOrgId: null,
  settings: {},
  defaultLanguage: 'zh_CN',
  languageList: [ // 语言列表
    {code: 'zh_CN', name: '简体中文', translations: {}},
    {code: 'zh_TW', name: '繁體中文', translations: {}},
    {code: 'en_US', name: 'English', translations: {}}
  ],

  // 获取域名配置的orgId
  getDomainOrgId() {
    if (this.matchOrgId !== null) return $q.resolve(this.matchOrgId)

    return (this.useCustomSettings ? loadJSONSync('json/custom/custom_domains.json') : $q.resolve({}))
      .then(domainBindings => {
        const matchOrgId = parseDomainOrgId(domainBindings, location.hostname) || ''
        this.matchOrgId = matchOrgId
        return matchOrgId
      })
  },

  // 获取客户端配置（用户组织级 > 域名级 > 自定义 > 默认）
  getClientConf() {
    if (Object.keys(this.settings || {}).length) return $q.resolve(this.settings)

    const filePathList = ['json/settings.json', '', '']

    if (this.useCustomSettings) {
      filePathList[1] = 'json/custom/custom_settings.json'
      filePathList[2] = this.matchOrgId ? 'json/custom/custom_settings.' + this.matchOrgId + '.json' : ''
    }

    return loadJSONSync(filePathList).then(res => {
      const settings = merge(...res)
      this.settings = settings
      return settings
    })
  },

  // 获取用户的客户端配置
  getUserClientConf(orgId) {
    if (!this.useCustomSettings || !orgId) return $q.resolve({})

    return loadJSONSync('json/custom/custom_settings.' + orgId + '.json').then(res => {
      const settings = merge(this.settings, res)
      this.settings = settings
      return res
    })
  },

  // 获取翻译文件配置（域名级 > 自定义 > 默认）
  getI18nConf(languageCode) {
    const that = this

    // 添加支持的语言列表
    function addLanguages(languages = []) {
      that.languageList = uniqBy(that.languageList.concat(languages), 'code')
    }
    // 存在浏览器返回都是小写，如 zh_cn，故以languageList的code为准
    function at(code) {
      if (!code) return

      code = String(code)
      const language = find(that.languageList, item => item.code.toLowerCase() === code.toLowerCase())
      if (!language) console.warn(`languageCode: ${code} is invalid!`, that.languageList)
      return language && language.code
    }
    // 设置默认语言
    function setDefaultLanguage(settingLanguage) {
      const storageLanguage = getLocalValue('localStorage', 'NG_TRANSLATE_LANG_KEY') ||
                          getLocalValue('cookie', 'NG_TRANSLATE_LANG_KEY') ||
                          getLocalValue('cookie', 'locale')
      let navigatorLanguage = navigator.language.replace('-', '_')
      navigatorLanguage = startsWith(navigatorLanguage, 'en') ? 'en_US' : navigatorLanguage
      settingLanguage = (settingLanguage && settingLanguage !== 'auto') ? settingLanguage : null
      const defaultLanguage = at(storageLanguage) || at(settingLanguage) || at(navigatorLanguage) || that.defaultLanguage // 默认语言
      that.defaultLanguage = defaultLanguage
    }

    // 如果languageCode不存在或者不合法就读取默认语言
    languageCode = at(languageCode)
    if (!languageCode) {
      addLanguages(that.settings.addLanguages || [])
      setDefaultLanguage(that.settings.defaultLanguage)
      languageCode = that.defaultLanguage
    }

    const index = findIndex(that.languageList, item => (item.code === languageCode))
    if (index === -1) return $q.resolve({})
    if (Object.keys(that.languageList[index].translations || {}).length) {
      console.log('load translations cache')
      return $q.resolve(that.languageList[index].translations)
    }

    const filePathList = [`json/i18n/locale-${languageCode}.json`, '', '']
    if (that.useCustomSettings) {
      filePathList[1] = `json/custom/custom_locale-${languageCode}.json`
      filePathList[2] = that.matchOrgId ? `json/custom/custom_${that.matchOrgId}_locale-${languageCode}.json` : ''
    }

    return loadJSONSync(filePathList).then(res => {
      const translations = merge(...res)
      that.languageList[index].translations = translations
      return translations
    })
  }
}



// WEBPACK FOOTER //
// ./src/js/global/config.js